"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import React, { useCallback, useMemo, useSyncExternalStore } from "react";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import BrainMobileTabs from "./mobile/BrainMobileTabs";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import BrainDesktopDrop from "./BrainDesktopDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { useWaveData } from "@/hooks/useWaveData";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import MemesQuickVoteDialog from "./left-sidebar/waves/memes-quick-vote/MemesQuickVoteDialog";
import { getActiveWaveIdFromUrl, getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { markDropCloseNavigation } from "@/helpers/drop-close-navigation.helpers";
import CreateWaveModal from "@/components/waves/create-wave/CreateWaveModal";
import CreateDirectMessageModal from "@/components/waves/create-dm/CreateDirectMessageModal";
import { useAuth } from "@/components/auth/Auth";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
import { useClosingDropId } from "@/hooks/useClosingDropId";
import { useMemesQuickVoteDialogController } from "@/hooks/useMemesQuickVoteDialogController";
import BrainMobileViewContent from "./mobile/BrainMobileViewContent";
import { BrainView } from "./mobile/brainMobileViews";
import { useBrainMobileActiveView } from "./mobile/useBrainMobileActiveView";
import { DROP_DETAIL_STALE_TIME_MS, fetchDropByIdBatched, getDropQueryKey } from "@/services/api/drop-api";
interface Props {
  readonly children: ReactNode;
}
const BrainMobile: React.FC<Props> = t0 => {
  const $ = _c(96);
  const {
    children
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const {
    connectedProfile
  } = useAuth();
  const hasAuthenticatedProfile = Boolean(connectedProfile?.handle);
  const quickVote = useMemesQuickVoteDialogController();
  const hydrated = useSyncExternalStore(_temp2, _temp3, _temp4);
  const myStream = useMyStreamOptional();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("drop") ?? undefined;
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const dropId = t1;
  const {
    effectiveDropId,
    beginClosingDrop
  } = useClosingDropId(dropId);
  let t2;
  if ($[2] !== effectiveDropId) {
    t2 = getDropQueryKey(effectiveDropId);
    $[2] = effectiveDropId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== effectiveDropId) {
    t3 = () => {
      if (!effectiveDropId) {
        throw new Error("Cannot fetch drop without a drop id");
      }
      return fetchDropByIdBatched(effectiveDropId);
    };
    $[4] = effectiveDropId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !!effectiveDropId;
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      placeholderData: keepPreviousData,
      enabled: t4,
      staleTime: DROP_DETAIL_STALE_TIME_MS
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    data: drop
  } = useQuery(t5);
  let t6;
  if ($[10] !== myStream?.activeWave.id || $[11] !== pathname || $[12] !== searchParams) {
    t6 = myStream?.activeWave.id ?? getActiveWaveIdFromUrl({
      pathname,
      searchParams
    }) ?? null;
    $[10] = myStream?.activeWave.id;
    $[11] = pathname;
    $[12] = searchParams;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const waveId = t6;
  let t7;
  if ($[14] !== isApp || $[15] !== pathname || $[16] !== router || $[17] !== searchParams) {
    t7 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[14] = isApp;
    $[15] = pathname;
    $[16] = router;
    $[17] = searchParams;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7 || $[20] !== waveId) {
    t8 = {
      waveId,
      onWaveNotFound: t7
    };
    $[19] = t7;
    $[20] = waveId;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const {
    data: wave
  } = useWaveData(t8);
  const {
    isMemesWave,
    isCurationWave,
    isRankWave
  } = useWave(wave);
  const {
    voting: t9,
    decisions: t10
  } = useWaveTimers(wave);
  const {
    isCompleted
  } = t9;
  const {
    firstDecisionDone
  } = t10;
  let t11;
  if ($[22] !== firstDecisionDone || $[23] !== hasAuthenticatedProfile || $[24] !== isApp || $[25] !== isCompleted || $[26] !== isCurationWave || $[27] !== isMemesWave || $[28] !== isRankWave || $[29] !== pathname || $[30] !== searchParams || $[31] !== wave || $[32] !== waveId) {
    t11 = {
      firstDecisionDone,
      isApp,
      isCompleted,
      hasAuthenticatedProfile,
      isCurationWave,
      isMemesWave,
      isRankWave,
      pathname,
      searchParams,
      wave,
      waveId
    };
    $[22] = firstDecisionDone;
    $[23] = hasAuthenticatedProfile;
    $[24] = isApp;
    $[25] = isCompleted;
    $[26] = isCurationWave;
    $[27] = isMemesWave;
    $[28] = isRankWave;
    $[29] = pathname;
    $[30] = searchParams;
    $[31] = wave;
    $[32] = waveId;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  const {
    activeView,
    onViewChange
  } = useBrainMobileActiveView(t11);
  let t12;
  if ($[34] !== pathname || $[35] !== router || $[36] !== searchParams) {
    t12 = selectedDrop => {
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", selectedDrop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[34] = pathname;
    $[35] = router;
    $[36] = searchParams;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  const onDropClick = t12;
  let t13;
  if ($[38] !== beginClosingDrop || $[39] !== dropId || $[40] !== pathname || $[41] !== router || $[42] !== searchParams) {
    t13 = () => {
      if (dropId) {
        beginClosingDrop(dropId);
      }
      markDropCloseNavigation();
      const params_1 = new URLSearchParams(searchParams.toString() || "");
      params_1.delete("drop");
      const newUrl_0 = params_1.toString() ? `${pathname}?${params_1.toString()}` : pathname || getHomeRoute();
      router.replace(newUrl_0, {
        scroll: false
      });
    };
    $[38] = beginClosingDrop;
    $[39] = dropId;
    $[40] = pathname;
    $[41] = router;
    $[42] = searchParams;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  const onDropClose = t13;
  let t14;
  if ($[44] !== drop || $[45] !== effectiveDropId) {
    t14 = !!effectiveDropId && !!drop && drop.id.toLowerCase() === effectiveDropId.toLowerCase();
    $[44] = drop;
    $[45] = effectiveDropId;
    $[46] = t14;
  } else {
    t14 = $[46];
  }
  const isDropOpen = t14;
  const hasWave = Boolean(waveId);
  let t15;
  if ($[47] !== pathname || $[48] !== router || $[49] !== searchParams) {
    t15 = () => {
      const params_2 = new URLSearchParams(searchParams.toString() || "");
      params_2.delete("create");
      const base = pathname;
      const next = params_2.toString() ? `${base}?${params_2.toString()}` : base;
      router.replace(next, {
        scroll: false
      });
    };
    $[47] = pathname;
    $[48] = router;
    $[49] = searchParams;
    $[50] = t15;
  } else {
    t15 = $[50];
  }
  const closeCreateOverlay = t15;
  let t16;
  bb0: {
    if (!isApp) {
      t16 = null;
      break bb0;
    }
    const createParam = searchParams.get("create");
    if (!createParam) {
      t16 = null;
      break bb0;
    }
    if (!connectedProfile) {
      t16 = null;
      break bb0;
    }
    if (createParam === "dm") {
      let t17;
      if ($[51] !== closeCreateOverlay || $[52] !== connectedProfile) {
        t17 = <CreateDirectMessageModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[51] = closeCreateOverlay;
        $[52] = connectedProfile;
        $[53] = t17;
      } else {
        t17 = $[53];
      }
      t16 = t17;
      break bb0;
    }
    if (createParam === "wave") {
      let t17;
      if ($[54] !== closeCreateOverlay || $[55] !== connectedProfile) {
        t17 = <CreateWaveModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[54] = closeCreateOverlay;
        $[55] = connectedProfile;
        $[56] = t17;
      } else {
        t17 = $[56];
      }
      t16 = t17;
      break bb0;
    }
    t16 = null;
  }
  const createOverlay = t16;
  const shouldMountQuickVoteDialog = quickVote.isQuickVoteOpen || activeView === BrainView.WAVES;
  const dropOverlayClass = isApp ? "tw-fixed tw-inset-0 tw-z-[1010] tw-bg-black tailwind-scope" : "tw-absolute tw-inset-0 tw-z-[1010]";
  let t17;
  if ($[57] !== drop || $[58] !== dropOverlayClass || $[59] !== isDropOpen || $[60] !== onDropClose) {
    t17 = isDropOpen && <div className={dropOverlayClass}><BrainDesktopDrop drop={{
        type: DropSize.FULL,
        ...drop,
        stableKey: drop.id,
        stableHash: drop.id
      }} onClose={onDropClose} /></div>;
    $[57] = drop;
    $[58] = dropOverlayClass;
    $[59] = isDropOpen;
    $[60] = onDropClose;
    $[61] = t17;
  } else {
    t17 = $[61];
  }
  let t18;
  if ($[62] !== activeView || $[63] !== hasWave || $[64] !== hydrated || $[65] !== isApp || $[66] !== onViewChange || $[67] !== wave) {
    t18 = (hasWave || !isApp) && <BrainMobileTabs activeView={activeView} onViewChange={onViewChange} wave={wave} waveActive={hasWave} showWavesTab={hydrated} showStreamBack={hydrated} isApp={isApp} />;
    $[62] = activeView;
    $[63] = hasWave;
    $[64] = hydrated;
    $[65] = isApp;
    $[66] = onViewChange;
    $[67] = wave;
    $[68] = t18;
  } else {
    t18 = $[68];
  }
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      opacity: 0,
      x: 20
    };
    t20 = {
      opacity: 1,
      x: 0
    };
    t21 = {
      opacity: 0,
      x: -20
    };
    t22 = {
      duration: 0.2,
      ease: "easeInOut"
    };
    $[69] = t19;
    $[70] = t20;
    $[71] = t21;
    $[72] = t22;
  } else {
    t19 = $[69];
    t20 = $[70];
    t21 = $[71];
    t22 = $[72];
  }
  let t23;
  if ($[73] !== activeView || $[74] !== children || $[75] !== isCurationWave || $[76] !== isMemesWave || $[77] !== isRankWave || $[78] !== onDropClick || $[79] !== quickVote.openQuickVote || $[80] !== quickVote.prefetchQuickVote || $[81] !== wave || $[82] !== waveId) {
    t23 = <BrainMobileViewContent activeView={activeView} activeWaveId={waveId} isCurationWave={isCurationWave} isMemesWave={isMemesWave} isRankWave={isRankWave} onDropClick={onDropClick} onOpenQuickVote={quickVote.openQuickVote} onPrefetchQuickVote={quickVote.prefetchQuickVote} wave={wave}>{children}</BrainMobileViewContent>;
    $[73] = activeView;
    $[74] = children;
    $[75] = isCurationWave;
    $[76] = isMemesWave;
    $[77] = isRankWave;
    $[78] = onDropClick;
    $[79] = quickVote.openQuickVote;
    $[80] = quickVote.prefetchQuickVote;
    $[81] = wave;
    $[82] = waveId;
    $[83] = t23;
  } else {
    t23 = $[83];
  }
  let t24;
  if ($[84] !== activeView || $[85] !== t23) {
    t24 = <LazyMotion features={domAnimation}><AnimatePresence mode="wait"><m.div key={activeView} initial={t19} animate={t20} exit={t21} transition={t22} className="tw-relative tw-min-w-0 tw-flex-1">{t23}</m.div></AnimatePresence></LazyMotion>;
    $[84] = activeView;
    $[85] = t23;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] !== quickVote.dialogState || $[88] !== shouldMountQuickVoteDialog) {
    t25 = shouldMountQuickVoteDialog && <MemesQuickVoteDialog {...quickVote.dialogState} />;
    $[87] = quickVote.dialogState;
    $[88] = shouldMountQuickVoteDialog;
    $[89] = t25;
  } else {
    t25 = $[89];
  }
  let t26;
  if ($[90] !== createOverlay || $[91] !== t17 || $[92] !== t18 || $[93] !== t24 || $[94] !== t25) {
    t26 = <div className="tw-relative tw-flex tw-h-full tw-flex-col">{createOverlay}{t17}{t18}{t24}{t25}</div>;
    $[90] = createOverlay;
    $[91] = t17;
    $[92] = t18;
    $[93] = t24;
    $[94] = t25;
    $[95] = t26;
  } else {
    t26 = $[95];
  }
  return t26;
};
export default BrainMobile;
function _temp() {}
function _temp2() {
  return _temp;
}
function _temp3() {
  return true;
}
function _temp4() {
  return false;
}